/**
 * Narrowing the library: which owner, which kind, and a free-text match.
 *
 * The owner filter is not cosmetic. Media lives in one partition per store plus
 * the shared one, so choosing a single owner is also choosing to make one
 * request instead of six — which is why the page passes the selection down to
 * its queries rather than filtering a list it already holds.
 */

import { useId } from 'react';
import type { ReactElement } from 'react';
import { SHARED_SCOPE_PARAM } from '../../lib/media.js';
import type { MediaKindFilter } from './presentation.js';
import type { ScopeOption } from './MediaDropzone.js';

export const ALL_SCOPES = 'all';

export type ScopeSelection = typeof ALL_SCOPES | string;

export interface MediaFiltersProps {
  scopes: readonly ScopeOption[];
  scope: ScopeSelection;
  onScopeChange: (scope: ScopeSelection) => void;
  kind: MediaKindFilter;
  onKindChange: (kind: MediaKindFilter) => void;
  query: string;
  onQueryChange: (query: string) => void;
  /** How many rows survive the current filter, for the count beside it. */
  shown: number;
  total: number;
}

const KINDS: readonly { value: MediaKindFilter; label: string }[] = [
  { value: 'all', label: 'Everything' },
  { value: 'video', label: 'Video' },
  { value: 'image', label: 'Images' },
];

export function scopeOptionValue(storeId: string | null): string {
  return storeId ?? SHARED_SCOPE_PARAM;
}

/** The scopes a selection expands to, which is what the page queries. */
export function selectedScopes(
  scopes: readonly ScopeOption[],
  selection: ScopeSelection,
): ScopeOption[] {
  if (selection === ALL_SCOPES) return [...scopes];
  return scopes.filter((scope) => scopeOptionValue(scope.storeId) === selection);
}

export function MediaFilters({
  scopes,
  scope,
  onScopeChange,
  kind,
  onKindChange,
  query,
  onQueryChange,
  shown,
  total,
}: MediaFiltersProps): ReactElement {
  const scopeId = useId();
  const queryId = useId();

  return (
    <div className="media-toolbar">
      <label className="field field--inline" htmlFor={scopeId}>
        <span className="field__label">Owner</span>
        <select
          id={scopeId}
          className="input"
          value={scope}
          onChange={(event) => onScopeChange(event.target.value)}
        >
          <option value={ALL_SCOPES}>Everything I can reach</option>
          {scopes.map((option) => (
            <option key={scopeOptionValue(option.storeId)} value={scopeOptionValue(option.storeId)}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      <div className="field field--inline">
        <span className="field__label">Kind</span>
        <div className="segmented" role="group" aria-label="Filter by kind">
          {KINDS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={
                option.value === kind ? 'segmented__item is-selected' : 'segmented__item'
              }
              aria-pressed={option.value === kind}
              onClick={() => onKindChange(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <label className="field field--inline media-toolbar__search" htmlFor={queryId}>
        <span className="field__label">Search</span>
        <input
          id={queryId}
          className="input"
          type="search"
          value={query}
          placeholder="Title or brand"
          onChange={(event) => onQueryChange(event.target.value)}
        />
      </label>

      <p className="media-toolbar__count">
        {shown === total ? `${total} in view` : `${shown} of ${total}`}
      </p>
    </div>
  );
}
